import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../../../../api/hooks/useSmartAndRegularAssets";
import { entityKeys } from "../../../../../../../api/queryKeys";
import ImpactChip from "../../../ControlRemediationDrawer/control/ImpactChip";
import ControlEntitiesActionButtons from "../../shared/ControlEntitiesActionButtons";
import SearchTextField from "../../shared/SearchTextField";
import EntityTypeAndStatusTabbedTable from "../../tables/EntityTypeAndStatusTabbedTable";
import { MisconfigStatusTab } from "../../tables/MisconfigStatusTabs";
import MisconfigurationEvidenceModal from "./MisconfigurationEvidenceModal";
import { groupEntitiesByTypeAndStatus } from "./misconfigurationsSectionUtils";
import getMisconfigurationsTableColumns from "./misconfigurationsTableColumns";

export default function MisconfigurationsSection({
  fullControl,
  misconfiguredComplexEntitiesByType,
  handleMisconfigurationClick,
}) {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const [searchText, setSearchText] = useState("");
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    labels: {},
    assets: {},
  });
  const { regularAndActiveSmartAsset: assets } =
    useSmartAndRegularAssets(false);

  const entitiesByTypeAndStatus = groupEntitiesByTypeAndStatus(
    fullControl,
    misconfiguredComplexEntitiesByType,
    searchText,
    appliedFilters,
    assets,
    trackEvent
  );

  const [selectedEntityTypeTab, setSelectedEntityTypeTab] = useState(
    entitiesByTypeAndStatus ? Object.keys(entitiesByTypeAndStatus)[0] : null
  );
  const [selectedStatusTab, setSelectedStatusTab] = useState(
    MisconfigStatusTab.INCLUDED
  );
  const [checkedEntityIDs, setCheckedEntityIDs] = useState([]);

  const queryClient = useQueryClient();
  const terminatedEntities = queryClient.getQueryData(
    entityKeys.terminatedEntities()
  ).data;

  if (!entitiesByTypeAndStatus) {
    return (
      <Typography variant="body2" color={colors.primary}>
        Error loading this content...
      </Typography>
    );
  }

  const onEvidenceClick = (event, evidence) => {
    event.stopPropagation();
    trackEvent("User clicked a misconfiguration evidence");
    setEvidenceModalOpen(evidence);
  };

  const columns = getMisconfigurationsTableColumns(
    fullControl,
    selectedEntityTypeTab,
    handleMisconfigurationClick,
    onEvidenceClick,
    colors,
    terminatedEntities
  );

  const handleCheckedChange = (newCheckedEntityIDs) => {
    trackEvent(
      `User checked a misconfiguration of control ${fullControl.control_id}`
    );
    setCheckedEntityIDs(newCheckedEntityIDs);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box
        display="flex"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        marginTop={0.5}
      >
        <ImpactChip
          numberOfEntities={
            entitiesByTypeAndStatus[selectedEntityTypeTab]?.[selectedStatusTab]
              ?.length || 0
          }
        />
        <Box display="flex" alignItems="center" gap={1}>
          <SearchTextField
            searchText={searchText}
            setSearchText={setSearchText}
          />
          <ControlEntitiesActionButtons
            fullControl={fullControl}
            entitiesByTypeAndStatus={entitiesByTypeAndStatus}
            checkedEntityIDs={checkedEntityIDs}
            selectedEntityTypeTab={selectedEntityTypeTab}
            selectedStatusTab={selectedStatusTab}
            actionsOnMisconfigurations={true}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Box>
      </Box>
      <Box>
        <EntityTypeAndStatusTabbedTable
          entitiesByTypeAndStatus={entitiesByTypeAndStatus}
          columns={columns}
          selectedEntityTypeTab={selectedEntityTypeTab}
          setSelectedEntityTypeTab={setSelectedEntityTypeTab}
          selectedStatusTab={selectedStatusTab}
          setSelectedStatusTab={setSelectedStatusTab}
          handleCheckedChange={handleCheckedChange}
        />
      </Box>
      {evidenceModalOpen && (
        <MisconfigurationEvidenceModal
          controlId={fullControl?.control_id}
          evidence={evidenceModalOpen}
          handleClose={() => {
            setEvidenceModalOpen(false);
          }}
        />
      )}
    </Box>
  );
}
